/**
 * RolloverSnoozePopover.tsx
 *
 * A small popover that appears when the user clicks the "Rolling" (מגולגלת)
 * badge on a Known Action or Recurring Action Line row.
 *
 * It offers two actions:
 *   A) "דחה לתאריך..." — postpone to a future date (snooze)
 *   B) "בטל גלגול"      — disable rolling entirely (pin to original date)
 *
 * Props
 * ─────
 *  actionId       UUID of the row being acted on.
 *  sourceTable    'known' | 'recurring' — drives which service to call.
 *  currentSnooze  Current snoozed_until value (may be null).
 *  originalDate   Original action_date (YYYY-MM-DD) — used to enforce that
 *                 the snooze date must be strictly after the original date.
 *  onClose        Called when the popover should be dismissed.
 *  onUpdated      Called after a successful DB update (triggers table refresh).
 *
 * Positioning
 * ───────────
 * The popover is rendered at its natural DOM position (not portal-based).
 * Wrap the trigger button in `position: relative` and render this component
 * immediately after the trigger.  The popover floats below-left via absolute
 * positioning with a high z-index.
 */

import { useRef, useEffect, useState } from 'react';
import { Loader2, CalendarClock, BanIcon, X, AlarmClock } from 'lucide-react';
import { knownActionsService } from '../../lib/knownActionsService';
import { recurringActionsService } from '../../lib/recurringActionsService';
import { getTodayString } from '../../lib/rolloverUtils';

// ─── Quick-snooze preset helpers ─────────────────────────────────────────────

function addDays(base: string, days: number): string {
  const d = new Date(base + 'T00:00:00');
  d.setDate(d.getDate() + days);
  const yyyy = d.getFullYear();
  const mm   = String(d.getMonth() + 1).padStart(2, '0');
  const dd   = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function fmtDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface RolloverSnoozePopoverProps {
  actionId:      string;
  sourceTable:   'known' | 'recurring';
  currentSnooze: string | null | undefined;
  originalDate:  string;
  onClose:       () => void;
  onUpdated:     () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function RolloverSnoozePopover({
  actionId,
  sourceTable,
  currentSnooze,
  originalDate,
  onClose,
  onUpdated,
}: RolloverSnoozePopoverProps) {
  const today           = getTodayString();
  const tomorrow        = addDays(today, 1);

  // Date picker value — default to tomorrow, or current snooze if set
  const [pickerDate, setPickerDate] = useState<string>(
    currentSnooze && currentSnooze > today ? currentSnooze : tomorrow,
  );
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState<string | null>(null);

  const popoverRef = useRef<HTMLDivElement>(null);

  // ── Close on outside click ────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    // Use capture so it fires before any other click handlers
    document.addEventListener('mousedown', handler, true);
    return () => document.removeEventListener('mousedown', handler, true);
  }, [onClose]);

  // ── Close on Escape ───────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // ── Service dispatch ──────────────────────────────────────────────────────
  const svc = sourceTable === 'known' ? knownActionsService : recurringActionsService;

  const handleSnooze = async () => {
    if (!pickerDate || pickerDate <= today) {
      setError('בחר תאריך עתידי (מחר ואילך)');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const { error: err } = await svc.snoozeUntil(actionId, pickerDate);
      if (err) { setError(err.message); return; }
      onUpdated();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const handleCancelRollover = async () => {
    setSaving(true);
    setError(null);
    try {
      const { error: err } = await svc.toggleRollover(actionId, false /* currentDisabled=false → set to true */);
      if (err) { setError(err.message); return; }
      onUpdated();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const handleClearSnooze = async () => {
    setSaving(true);
    setError(null);
    try {
      const { error: err } = await svc.cancelSnooze(actionId);
      if (err) { setError(err.message); return; }
      onUpdated();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  // ── Quick-select presets ──────────────────────────────────────────────────
  const presets = [
    { label: 'מחר',     date: tomorrow },
    { label: '+3 ימים', date: addDays(today, 3) },
    { label: '+1 שבוע', date: addDays(today, 7) },
    { label: '+2 שבועות', date: addDays(today, 14) },
    { label: '+1 חודש', date: addDays(today, 30) },
  ];

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div
      ref={popoverRef}
      dir="rtl"
      className="absolute top-full left-0 mt-1 w-72 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 overflow-hidden"
      onClick={e => e.stopPropagation()}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-l from-amber-50 to-orange-50 border-b border-amber-100">
        <div className="flex items-center gap-2">
          <AlarmClock className="w-4 h-4 text-amber-600" />
          <span className="text-sm font-semibold text-amber-900">ניהול גלגול</span>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-0.5 rounded">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="p-4 flex flex-col gap-4">

        {/* ── Section A: Snooze to date ─────────────────────────── */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
            <CalendarClock className="w-4 h-4 text-blue-500" />
            <span>דחה לתאריך…</span>
          </div>

          {/* Currently snoozed banner */}
          {currentSnooze && currentSnooze > today && (
            <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-3 py-1.5 text-xs">
              <span className="text-blue-700">
                נדחה כרגע עד <strong>{fmtDate(currentSnooze)}</strong>
              </span>
              <button
                onClick={handleClearSnooze}
                disabled={saving}
                className="text-blue-500 hover:text-red-500 underline text-[11px]"
              >
                נקה
              </button>
            </div>
          )}

          {/* Quick presets */}
          <div className="flex flex-wrap gap-1.5">
            {presets.map(p => (
              <button
                key={p.date}
                onClick={() => setPickerDate(p.date)}
                className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                  pickerDate === p.date
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400 hover:text-blue-600'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* Date picker */}
          <input
            type="date"
            value={pickerDate}
            min={tomorrow}
            onChange={e => setPickerDate(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white
              focus:ring-2 focus:ring-blue-500 focus:border-transparent text-right"
          />

          <button
            onClick={handleSnooze}
            disabled={saving || !pickerDate || pickerDate <= today}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg
              bg-blue-600 text-white text-sm font-medium
              hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed
              focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
          >
            {saving
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <CalendarClock className="w-4 h-4" />
            }
            דחה ל־{pickerDate ? fmtDate(pickerDate) : '…'}
          </button>
        </div>

        {/* Divider */}
        <div className="flex items-center gap-2">
          <div className="flex-1 h-px bg-gray-200" />
          <span className="text-[11px] text-gray-400">או</span>
          <div className="flex-1 h-px bg-gray-200" />
        </div>

        {/* ── Section B: Cancel rolling entirely ───────────────── */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
            <BanIcon className="w-4 h-4 text-red-400" />
            <span>בטל גלגול</span>
          </div>
          <p className="text-[11px] text-gray-500 leading-relaxed">
            הפעולה תוצמד לתאריך המקורי (<strong>{fmtDate(originalDate)}</strong>) ולא תגלגל
            עוד.  ניתן להפעיל מחדש מכפתור ה-<RefreshCwIcon /> בשורה.
          </p>
          <button
            onClick={handleCancelRollover}
            disabled={saving}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg
              border border-red-300 text-red-600 bg-white text-sm font-medium
              hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed
              focus:outline-none focus:ring-2 focus:ring-red-400 transition-colors"
          >
            {saving
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <BanIcon className="w-4 h-4" />
            }
            בטל גלגול אוטומטי
          </button>
        </div>

        {/* Error */}
        {error && (
          <p className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2 border border-red-200">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}

// Inline icon helper (avoids import of RefreshCw just for the description text)
function RefreshCwIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="10" height="10" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round"
      className="inline-block align-text-bottom mx-0.5"
    >
      <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
      <path d="M21 3v5h-5" />
      <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
      <path d="M8 16H3v5" />
    </svg>
  );
}
